import { apiClient } from '../client';
import { MetadataType, MetadataItem, CreateMetadataRequest } from '../types';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('MetadataService');

export class MetadataService {
  /**
   * Fetch all metadata types and their items
   */
  async getMetadata(): Promise<MetadataType[]> {
    try {
      logger.debug('Fetching metadata...');
      const metadata = await apiClient.get<MetadataType[]>('/v1/metadata');
      logger.debug('Metadata fetched successfully:', metadata.length, 'types');
      return metadata;
    } catch (error) {
      logger.error('Failed to fetch metadata:', error);
      throw error;
    }
  }

  /**
   * Fetch all available metadata types
   */
  async getMetadataTypes(): Promise<MetadataType[]> {
    try {
      logger.debug('Fetching metadata types...');
      const types = await apiClient.get<MetadataType[]>('/v1/metadata/types');
      logger.debug('Metadata types fetched successfully:', types.length, 'types');
      return types;
    } catch (error) {
      logger.error('Failed to fetch metadata types:', error);
      throw error;
    }
  }

  /**
   * Create a new metadata item
   */
  async createMetadataItem(request: CreateMetadataRequest): Promise<MetadataItem> {
    try {
      logger.debug('Creating metadata item:', request);
      const item = await apiClient.post<MetadataItem>('/v1/metadata', request);
      logger.debug('Metadata item created successfully:', item);
      return item;
    } catch (error) {
      logger.error('Failed to create metadata item:', error);
      throw error;
    }
  }

  /**
   * Update an existing metadata item
   */
  async updateMetadataItem(
    typeKey: string, 
    itemKey: string, 
    updates: Partial<CreateMetadataRequest>
  ): Promise<MetadataItem> {
    try {
      logger.debug('Updating metadata item:', { typeKey, itemKey, updates });
      const item = await apiClient.put<MetadataItem>(
        `/v1/metadata/${typeKey}/${itemKey}`, 
        updates
      );
      logger.debug('Metadata item updated successfully:', item);
      return item;
    } catch (error) {
      logger.error('Failed to update metadata item:', error);
      throw error;
    }
  }

  /**
   * Delete a metadata item
   */
  async deleteMetadataItem(typeKey: string, itemKey: string): Promise<void> {
    try {
      logger.debug('Deleting metadata item:', { typeKey, itemKey });
      await apiClient.delete(`/v1/metadata/${typeKey}/${itemKey}`);
      logger.debug('Metadata item deleted successfully');
    } catch (error) {
      logger.error('Failed to delete metadata item:', error);
      throw error;
    }
  }

  /**
   * Get metadata items by type
   */
  async getMetadataItemsByType(typeKey: string): Promise<MetadataItem[]> {
    try {
      logger.debug('Fetching metadata items for type:', typeKey);
      const items = await apiClient.get<MetadataItem[]>(`/v1/metadata/${typeKey}`);
      logger.debug('Metadata items fetched successfully:', items.length, 'items');
      return items;
    } catch (error) {
      logger.error('Failed to fetch metadata items:', error);
      throw error;
    }
  }

  /**
   * Get a specific metadata item
   */
  async getMetadataItem(typeKey: string, itemKey: string): Promise<MetadataItem> {
    try {
      logger.debug('Fetching metadata item:', { typeKey, itemKey });
      const item = await apiClient.get<MetadataItem>(`/v1/metadata/${typeKey}/${itemKey}`);
      logger.debug('Metadata item fetched successfully:', item);
      return item;
    } catch (error) {
      logger.error('Failed to fetch metadata item:', error);
      throw error;
    }
  }

  /**
   * Search metadata items across all types
   */
  searchMetadata(metadata: MetadataType[], query: string): MetadataItem[] {
    const lowerQuery = query.toLowerCase();
    const results: MetadataItem[] = [];

    metadata.forEach(type => {
      if (type.items) {
        type.items.forEach(item => {
          if (
            item.name.toLowerCase().includes(lowerQuery) ||
            item.key.toLowerCase().includes(lowerQuery) ||
            item.description?.toLowerCase().includes(lowerQuery)
          ) {
            results.push(item);
          }
        });
      }
    });

    return results;
  }

  /**
   * Get metadata items by type from local data
   */
  getItemsByType(metadata: MetadataType[], typeKey: string): MetadataItem[] {
    const type = metadata.find(t => t.key === typeKey);
    return type?.items || [];
  }

  /**
   * Get metadata type by key from local data
   */
  getTypeByKey(metadata: MetadataType[], typeKey: string): MetadataType | undefined {
    return metadata.find(t => t.key === typeKey);
  }

  /**
   * Get metadata content
   */
  async getMetadataContent(typeKey: string, itemKey: string): Promise<string> {
    try {
      logger.debug('Fetching metadata content:', { typeKey, itemKey });
      const content = await apiClient.get<string>(`/v1/metadata/${typeKey}/${itemKey}`);
      logger.debug('Metadata content fetched successfully');
      return content;
    } catch (error) {
      logger.error('Failed to fetch metadata content:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const metadataService = new MetadataService(); 